import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Z_INDICES } from '../lib/zIndices';

/**
 * Premium bottom-sheet welcome tour. Renders an array of slides that the
 * user can swipe / arrow through. Each slide has an emoji-free icon
 * element, a title and a body. Used for first-time onboarding of guests
 * and hosts (see GuestHomePage and HostGenerateQRPage).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - slides: Array<{ key, icon: JSX, title, body }>
 *  - accentColor?: string  (default Allure purple)
 *  - testIdPrefix?: string (default 'welcome-tour')
 */
export default function WelcomeTourSheet({
  open,
  onClose,
  slides,
  accentColor = 'var(--accent-purple)',
  testIdPrefix = 'welcome-tour',
}) {
  const [index, setIndex] = useState(0);
  const total = slides?.length || 0;
  const slide = slides?.[index];
  const isLast = index === total - 1;

  // Reset to first slide every time the sheet opens
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const goNext = useCallback(() => {
    if (isLast) {
      onClose?.();
      return;
    }
    setIndex((current) => Math.min(current + 1, total - 1));
  }, [isLast, onClose, total]);

  const goPrev = useCallback(() => {
    setIndex((current) => Math.max(current - 1, 0));
  }, []);

  const handleSwipe = useCallback(
    (_event, info) => {
      const swipe = info.offset.x;
      if (swipe < -60) goNext();
      else if (swipe > 60) goPrev();
    },
    [goNext, goPrev],
  );

  const dots = useMemo(
    () =>
      Array.from({ length: total }).map((_, dotIndex) => (
        <span
          key={dotIndex}
          data-testid={`${testIdPrefix}-dot-${dotIndex}`}
          style={{
            width: dotIndex === index ? 22 : 8,
            height: 8,
            borderRadius: 9999,
            background: dotIndex === index ? accentColor : 'var(--card-border)',
            transition: 'width 220ms ease, background 220ms ease',
          }}
        />
      )),
    [accentColor, index, testIdPrefix, total],
  );

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          data-testid={`${testIdPrefix}-overlay`}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10, 10, 16, 0.18)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: Z_INDICES.modalOverlay,
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose?.();
          }}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            data-testid={`${testIdPrefix}-sheet`}
            style={{
              width: '100%',
              maxWidth: 480,
              background: 'var(--sheet-glass)',
              backdropFilter: 'saturate(180%) blur(28px)',
              WebkitBackdropFilter: 'saturate(180%) blur(28px)',
              borderTopLeftRadius: 30,
              borderTopRightRadius: 30,
              padding: '14px 22px calc(env(safe-area-inset-bottom, 0px) + 22px)',
              boxShadow: '0 -18px 40px rgba(0, 0, 0, 0.25)',
              borderTop: '1px solid var(--card-border)',
              position: 'relative',
            }}
          >
            {/* Grabber */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <span style={{ width: 44, height: 5, borderRadius: 9999, background: 'var(--card-border)' }} />
            </div>

            {/* Close (X) */}
            <button
              type="button"
              data-testid={`${testIdPrefix}-close-button`}
              aria-label="Close"
              onClick={onClose}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '1px solid var(--card-border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={16} strokeWidth={2.1} />
            </button>

            {/* Slide content (swipeable) */}
            <motion.div
              key={slide?.key || index}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.35}
              onDragEnd={handleSwipe}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.28 }}
              style={{
                paddingTop: 4,
                paddingBottom: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                userSelect: 'none',
              }}
            >
              <div
                data-testid={`${testIdPrefix}-slide-icon`}
                style={{
                  width: 110,
                  height: 110,
                  borderRadius: '50%',
                  background: 'var(--accent-surface, #F4F0FF)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: accentColor,
                  marginTop: 6,
                  marginBottom: 18,
                }}
              >
                {slide?.icon}
              </div>

              <h2
                data-testid={`${testIdPrefix}-slide-title`}
                style={{
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 24,
                  fontWeight: 700,
                  color: 'var(--text)',
                  margin: 0,
                  lineHeight: 1.16,
                  maxWidth: 320,
                }}
              >
                {slide?.title}
              </h2>

              <p
                data-testid={`${testIdPrefix}-slide-body`}
                style={{
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 16,
                  color: 'var(--text-2)',
                  margin: '12px 0 22px',
                  lineHeight: 1.5,
                  maxWidth: 340,
                }}
              >
                {slide?.body}
              </p>
            </motion.div>

            {/* Dots */}
            <div
              data-testid={`${testIdPrefix}-dots`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginBottom: 18,
              }}
            >
              {dots}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                type="button"
                data-testid={`${testIdPrefix}-prev-button`}
                onClick={goPrev}
                disabled={index === 0}
                aria-label="Previous"
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  border: '1px solid var(--card-border)',
                  background: 'var(--surface)',
                  color: index === 0 ? 'var(--text-3)' : accentColor,
                  cursor: index === 0 ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: 'var(--premium-shadow-main)',
                  opacity: index === 0 ? 0.6 : 1,
                }}
              >
                <ChevronLeft size={20} strokeWidth={2.2} />
              </button>

              <button
                type="button"
                data-testid={`${testIdPrefix}-next-button`}
                onClick={goNext}
                style={{
                  flex: 1,
                  height: 50,
                  borderRadius: 9999,
                  border: 'none',
                  background: accentColor,
                  color: '#FFFFFF',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 17,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: 'var(--premium-shadow-main)',
                }}
              >
                {isLast ? "Let's go" : 'Next'}
                {isLast ? null : <ChevronRight size={18} strokeWidth={2.2} />}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
